import { useMemo } from 'react'
import { permissionCheck } from '../../../../../../../../../../baseComponents/PermissionChecker/function'
import { ITDiscountDisplay, ITTypeQuantityDisplay } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/Components/Displays'
import { InputTableLayoutStepProps } from '../../../../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { CustomRenderBoolean, CustomRenderString } from '../../../../../../../../../../baseComponents/TwinTable/VirtualTableListing/CustomRenders'
import { ColumnOrderTableSchema, OrderData } from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import OrderSellLayout from '../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout'
import { ProductExtendedTyInputTableListing } from '../../../../../../../../../../specificComponents/Product/types'
import { dictionaryComplexFromJsonArr } from '../../../../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getAllWorkingCenters, getUserPermissions } from '../../../../../../../../../../utils/reducers/getters'
import SellDeliveryNoteCreateNoPermissionStepITHeader from './SellDeliveryNCreateNoPermissionStepITHeader'
import { SellDeliveryNoteProductModelTypeExtended } from '@teinor/erp/types/company/order/sell/sellDeliveryNote/sellDeliveryNoteProduct'
import useSellDeliveryNoteHeaderUrls from '../../../../../../../../../../utils/hooks/sells/orders/useSellDeliveryNoteHeaderUrls'
import useOrderRowRenderer from '../../../../../../../../../../utils/hooks/orders/useOrderRowRenderer'
import { sellDeliveryNoteStatusInversed } from '@teinor/erp/types/company/order/sell/sellDeliveryNote'
import SellDeliveryNoteCreateNoPermissionStepHeaderDecider from './SellDeliveryNCreateNoPermissionHeader'

interface SellDeliveryNoteCreateNoPermissionStepProps extends InputTableLayoutStepProps {
}

const SellDeliveryNoteCreateNoPermissionStep: React.FC<SellDeliveryNoteCreateNoPermissionStepProps> = ({ inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, setTableConfiguration, userPermissions, headerChanges }) => {
    const { t } = useTwinTranslation()
    const { rowRenderer } = useSellDeliveryNoteCreateNoPermissionStepLogic({ completeData })
    const accounts = useMemo(() => dictionaryComplexFromJsonArr(completeData?.header?.OrderAccounts), [completeData?.header?.OrderAccounts])
    const orderHeaderURLs = useSellDeliveryNoteHeaderUrls()
    if (!completeData) {
        return null
    }
    const priceColumns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellDeliveryNoteProductModelTypeExtended> = []
    const priceColumns2: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellDeliveryNoteProductModelTypeExtended> = []
    const statusColumn: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellDeliveryNoteProductModelTypeExtended> = []
    const workingCenters = getAllWorkingCenters()
    const dictWarehouses = workingCenters?.[completeData?.header?.WorkingCenter?.id]?.dictWarehouses
    const hidePrices = completeData?.header?.hidePrices
    if (!hidePrices) {
        priceColumns.push(
            {
                id: 'SpecificPriceRuleId',
                dataKey: 'SpecificPriceRuleId',
                label: t('priceRule', 'Tarifa'),
                customRender: (parameterValue, allRowData) => <CustomRenderString value={'T' + allRowData?.['SpecificPriceRule.id'] + ' - ' + allRowData?.['SpecificPriceRule.name']} />,
                hideColumnByDefault: true
            },
            {
                id: 'initialUnitPriceWithoutTax',
                dataKey: 'initialUnitPriceWithoutTax',
                label: () => <div className='text-right mr-15 w-full'>{t('price', 'Precio')}</div>,
                simpleLabel: t('price', 'Precio'),
                component: 'ITDisplayPrice',
                width: 100,
            },
        )
        priceColumns2.push(
            {
                id: 'OrderAccountId',
                dataKey: 'OrderAccountId',
                label: t('chapter', 'Capítulo'),
                customRender: (parameterValue) => <CustomRenderString value={accounts[parameterValue]?.name || ''} />,
                hideColumnByDefault: true,
            },
            {
                id: 'discount',
                dataKey: 'discount',
                label: t('discount', 'Descuento'),
                width: 90,
                customRender: (parameterValue, allRowData) => <ITDiscountDisplay CurrencyId={completeData?.header.CurrencyId} value={parameterValue} typeDiscount={allRowData.typeDiscount} />
            },
            {
                id: 'rowPriceWithoutTaxPrev',
                dataKey: 'rowPriceWithoutTaxPrev',
                label: () => <div className='text-right mr-15 w-full'>{t('rowPrice', 'Precio Línea')}</div>,
                simpleLabel: t('rowPrice', 'Precio Línea'),
                component: 'ITDisplayFinalPrice',
                with_vat: false,
                width: 120,
                hideColumnByDefault: true
            },
            {
                id: 'rowPriceWithoutTaxAndTotalDiscount',
                dataKey: 'rowPriceWithoutTaxAndTotalDiscount',
                label: () => <div className='text-right w-full'>{t('priceTotal', 'Precio Total')}</div>,
                simpleLabel: t('priceTotal', 'Precio Total'),
                component: 'ITDisplayFinalPrice',
                with_vat: false,
                width: 120,
            },
            {
                id: 'rowPriceWithTaxAndTotalDiscount',
                dataKey: 'rowPriceWithTaxAndTotalDiscount',
                label: () => <div className='text-right w-full'>{t('totalPriceWithTaxShort', 'Precio Total Imp.')}</div>,
                simpleLabel: t('totalPriceWithTaxShort', 'Precio Total Imp.'),
                component: 'ITDisplayFinalPrice',
                with_vat: false,
                width: 130,
                hideColumnByDefault: true,
            },
            {
                id: 'totalWithTaxWithComplements',
                dataKey: 'totalWithTaxWithComplements',
                simpleLabel: t('totalWithComplementsAndVat', 'Total con complementos e imp.'),
                label: () => <div className='text-right w-full' >{t('withComplementsAndVatS', 'Con comp. e imp.')}</div>,
                component: 'ITDisplayPrice',
                width: 160,
                hideColumnByDefault: true,
                toolTip: t('totalWithComplementsAndVatL', 'Total con complementos e impuestos'),
            },
            {
                id: 'totalWithoutTaxWithComplements',
                dataKey: 'totalWithoutTaxWithComplements',
                simpleLabel: t('totalWithComplements', 'Total con complementos'),
                label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
                component: 'ITDisplayPrice',
                width: 110,
                hideColumnByDefault: true,
                toolTip: t('totalWithComplements', 'Total con complementos')
            },
            {
                id: 'totalWithoutTaxWithComplementsPrev',
                dataKey: 'totalWithoutTaxWithComplementsPrev',
                simpleLabel: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de desc.'),
                label: () => <div className='text-right w-full'> {t('withComplementsS', 'Con comp.')}</div>,
                component: 'ITDisplayPrice',
                width: 110,
                hideColumnByDefault: true,
                toolTip: t('totalWithoutTaxWithComplementsPrev', 'Total con complementos antes de descuentos'),
            },
            {
                id: 'unitPriceWithComplements',
                dataKey: 'unitPriceWithComplements',
                simpleLabel: t('unitPriceWithComplements', 'Precio unitario con complementos'),
                label: () => <div className='text-right w-full'> {t('unitPricewithComplementsS', 'P. U. comp.')}</div>,
                component: 'ITDisplayPrice',
                width: 110,
                hideColumnByDefault: true,
                toolTip: t('unitPriceWithComplements', 'Precio unitario con complementos'),
            },
            {
                id: 'WarehouseId',
                dataKey: 'WarehouseId',
                label: t('warehouse', 'Almacén'),
                customRender: (parameterValue) => <CustomRenderString value={dictWarehouses?.[parseInt(parameterValue)]?.name || ''} />,
                hideColumnByDefault: true
            },
        )
    }
    if (completeData?.header.WorkSheetId || completeData?.header?.relatedWorkSheets?.length) {
        statusColumn.push(
            {
                id: 'haveWorkSheetProductStatus',
                dataKey: 'haveWorkSheetProductStatus',
                label: '',
                simpleLabel: t('workSheetStatus', 'Estado hoja de trabajo'),
                component: 'OrderProductWorkSheetStatus',
                hideColumnByDefault: true,
                Order: completeData?.header || {}
            },
        )
    }
    const columns: ColumnOrderTableSchema<ProductExtendedTyInputTableListing & SellDeliveryNoteProductModelTypeExtended> = [
        {
            id: 'nLine',
            dataKey: 'nLine',
            label: t('line', 'Línea'),
            width: 70,
            hideColumnByDefault: true
        },
        {
            id: 'url',
            dataKey: 'url',
            label: t('image', 'Imagen'),
            component: 'ITDisplayImage',
            width: 70
        },
        {
            id: 'code_bar',
            dataKey: 'code_bar',
            label: t('codeBarShort', 'Cod. de barras'),
            simpleLabel: t('codeBar', 'Código de barras'),
            component: 'OrderSearchProductInputCreate',
            field: 'code_bar',
            typeOnChange: 'onChangeAdd',
            sortable: true,
            hideColumnByDefault: true,
            minWidth: 115
        },
        {
            id: 'reference',
            dataKey: 'reference',
            label: t('reference', 'Referencia'),
            component: 'OrderSearchProductInput',
            field: 'reference',
            sortable: true,
            minWidth: 100,
            hideColumnByDefault: true
        },
        {
            id: 'name',
            dataKey: 'name',
            label: t('name', 'Nombre'),
            component: 'OrderSearchProductInputName',
            field: 'name',
            sortable: true,
            hideColumnByDefault: true
        },
        {
            id: 'referenceAndName',
            dataKey: 'name',
            label: t('referenceName', 'Referencia y nombre'),
            component: 'OrderSearchProductInputReferenceAndName',
            field: 'referenceAndName',
            sortable: true,
            minWidth: 180
        },
        {
            id: 'editProduct',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('buttonSeeProduct', 'Botón de ver producto'),
            component: 'OrderEditProduct',
            width: 35,
            havePermission: permissionCheck(getUserPermissions('product.profile.main'), 'read'),
        },
        {
            id: 'descriptionDocuments',
            dataKey: 'descriptionDocuments',
            label: t('description', 'Descripción'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'shortDescriptionDocuments',
            dataKey: 'shortDescriptionDocuments',
            label: t('shortDescriptionS', 'Desc. corta'),
            simpleLabel: t('shortDescription', 'Descripción corta'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'notes',
            dataKey: 'notes',
            label: t('notes', 'Notas'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue} />
        },
        ...priceColumns,
        {
            id: 'manualPrice',
            dataKey: 'manualPrice',
            label: t('manualPriceS', 'P. man.'),
            simpleLabel: t('manualPrice', 'Precio manual'),
            toolTip: t('manualPrice', 'Precio manual'),
            customRender: (parameterValue) => <CustomRenderBoolean value={String(parameterValue) === 'true'} width={60} />,
            hideColumnByDefault: true,
            width: 85,
        },
        {
            id: 'quantity',
            dataKey: 'quantity',
            label: t('quantity', 'Cantidad'),
            width: 110,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'quantity2',
            dataKey: 'quantity2',
            label: t('quantitySecondRes', 'Cant. sec.'),
            simpleLabel: t('quantitySecondary', 'Cantidad secundaria'),
            width: 90,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity2} value={parameterValue} />,
            hideColumnByDefault: true
        },
        {
            id: 'wasteWeight',
            dataKey: 'wasteWeight',
            label: t('wasteWeight', 'Merma'),
            customRender: (parameterValue) => <CustomRenderString value={parameterValue ? parameterValue + ' %' : ''} />,
            width: 100,
            hideColumnByDefault: true,
        },
        {
            id: 'calculatedQuantity',
            dataKey: 'calculatedQuantity',
            label: t('quantityCalculatedS', 'Cant. calc.'),
            toolTip: t('quantityCalculated', 'Cantidad calculada'),
            simpleLabel: t('quantityCalculated', 'Cantidad calculada'),
            width: 110,
            hideColumnByDefault: true,
            customRender: (parameterValue, allRowData) => <ITTypeQuantityDisplay measure={allRowData.typeQuantity} value={parameterValue} />
        },
        {
            id: 'PrintTags',
            dataKey: 'ProductId',
            label: '',
            simpleLabel: t('printTags', 'Imprimir etiquetas'),
            width: 33,
            component: 'OrderPrintTags',
            hideColumnByDefault: true
        },
        ...priceColumns2,
        ...statusColumn
    ]
    return (
        <OrderSellLayout modelId='SellDeliveryNoteId' name={'SellDeliveryNoteCreateNoPermissionStep' + (hidePrices ? 'hidePrices' : '')} translations={{ headerTitle: t('deliveryNote', 'Albarán') }} hideNotes={true} columns={columns}
        buttonSave={
            <SellDeliveryNoteCreateNoPermissionStepHeaderDecider completeData={completeData} isReadOnly={true} onSubmit={() => {}} data={completeData as OrderData} />
        } InputTableHeaderChildren={SellDeliveryNoteCreateNoPermissionStepITHeader} isReadOnly={true} {...{ orderHeaderURLs, rowRenderer, inputTableURLs, completeData, setCompleteData, getCompleteData, tableConfiguration, headerChanges, setTableConfiguration, userPermissions, orderStatusDict: sellDeliveryNoteStatusInversed }} />
    )
}

interface SellDeliveryNoteCreateNoPermissionStepLogicProps {
    completeData: OrderData | null
}

export const useSellDeliveryNoteCreateNoPermissionStepLogic = ({ completeData }: SellDeliveryNoteCreateNoPermissionStepLogicProps) => {
    const rowRenderer = useOrderRowRenderer({ completeData, isFinishedStep: true})

    return { rowRenderer }
}

export default SellDeliveryNoteCreateNoPermissionStep